import { Body1, Caption1, Textarea, makeStyles, tokens } from "@fluentui/react-components";
import { useEffect, useMemo, useState } from "react";
import { appGo } from "@/app/navigate";
import { AppDialog, DialogActionsBar, DialogField } from "@/components/app-dialog";
import { FuiInput } from "@/components/fui-control";
import { fmtCurrency, fmtDelta, fmtSpi } from "@/lib/format";
import { formatDate } from "@/lib/date-format";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { suggestPeriodName } from "./report-model";
import { contextFor, fmtSignedPp, useMaskedMoney } from "./report-ui";

const useStyles = makeStyles({
  preview: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  cell: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  label: { color: tokens.colorNeutralForeground3 },
  value: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  warn: { color: tokens.colorPaletteDarkOrangeForeground1 },
  hint: { color: tokens.colorNeutralForeground3 },
});

/** 보고 기간 마감: freezes the whole plan (WBS·일정·진척·인력·예산·Task) at the status date. */
export function CloseReportDialog() {
  const st = useStyles();
  const open = useProjectStore((s) => s.dialog === "report-close");
  const project = useProjectStore((s) => s.project);
  const reports = useProjectStore((s) => s.reports);
  const activeId = useProjectStore((s) => s.activeId);
  const resources = useCatalogStore((s) => s.resources);
  const masked = useMaskedMoney();
  const [name, setName] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (open) {
      setName("");
      setNote("");
    }
  }, [open]);

  const ctx = useMemo(() => (open ? contextFor(project, resources) : null), [open, project, resources]);
  const last = reports[0];
  const suggestion = suggestPeriodName(project.statusDate);
  const sameStatusDate = !!last && last.metrics.statusDate === project.statusDate;
  const close = () => useProjectStore.getState().openDialog(null);

  if (!ctx) return null;
  const m = ctx.metrics;

  return (
    <AppDialog
      open={open}
      onClose={close}
      title="보고 기간 마감"
      description="계획을 이 시점으로 고정합니다. 이후 보고와 비교됩니다."
      width="md"
      footer={
        <DialogActionsBar
          onCancel={close}
          confirmLabel="마감"
          onConfirm={() => {
            useProjectStore.getState().closeReportPeriod(name || suggestion, note);
            close();
            if (activeId) appGo(`/projects/${activeId}/reports`);
          }}
        />
      }
    >
      <DialogField label="이름">
        <FuiInput density="dialog" value={name} placeholder={suggestion} onChange={(_, d) => setName(d.value)} autoFocus />
      </DialogField>
      <DialogField label="메모" hint="보고서 요약 아래에 함께 표시돼요">
        <Textarea size="small" resize="vertical" value={note} placeholder="예: 설계 승인 1주 지연, 고객사 인터페이스 사양 회신 대기" onChange={(_, d) => setNote(d.value)} />
      </DialogField>
      <div className={st.preview} aria-label="마감될 지표">
        <Metric label="진척 기준일" value={formatDate(m.statusDate, "numeric")} />
        <Metric label="전체 진척" value={`${m.progress}%${last ? ` (${fmtSignedPp(m.progress - last.metrics.progress)})` : ""}`} />
        <Metric label="SPI · 종료 편차" value={m.hasBaseline ? `${fmtSpi(m.spi)} · ${fmtDelta(m.finishVarianceDays)}` : "기준선 없음"} />
        <Metric label="계획 비용" value={fmtCurrency(m.costTotal, masked)} />
      </div>
      {sameStatusDate ? (
        <Body1 className={st.warn}>직전 마감(「{last!.name}」)과 진척 기준일이 같아요. 진척 업데이트로 기준일을 옮긴 뒤 마감하면 기간 비교가 정확해져요.</Body1>
      ) : last ? (
        <Caption1 className={st.hint}>
          직전 마감 「{last.name}」 · {formatDate(last.metrics.statusDate, "numeric")} 이후 {Math.max(0, Math.round((Date.parse(project.statusDate) - Date.parse(last.metrics.statusDate)) / 86_400_000))}일
        </Caption1>
      ) : (
        <Caption1 className={st.hint}>첫 마감이에요. 다음 마감부터 이 시점과 비교됩니다.</Caption1>
      )}
    </AppDialog>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  const st = useStyles();
  return (
    <div className={st.cell}>
      <Caption1 className={st.label}>{label}</Caption1>
      <Body1 className={st.value} title={value}>
        {value}
      </Body1>
    </div>
  );
}
